import {
    CalendarOutlined,
    EnvironmentOutlined,
    PhoneOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { Avatar, Col, Empty, Input, Row, Select, Space, Spin, Tabs, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// API imports
import { getCustomerProduct } from '@/controller/warranty';
import Button from '@/shared/components/UI/Button';
import Modal from '@/shared/components/UI/Modal';
import { useTranslations } from '@/translations/useTranslations.js';

const ViewProductWarranty = () => {
    const navigate = useNavigate();

    const { t } = useTranslations();
    // States
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImg, setViewerImg] = useState(null);

    // Load products
    useEffect(() => {
        loadProducts();
    }, []);

    // Filter products when dependencies change
    useEffect(() => {
        let filtered = [...products];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(
                (product) =>
                    product.inventory?.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    product.inventory?.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()),
            );
        }

        // Apply tab filter
        if (activeTab !== 'all') {
            filtered = filtered.filter((product) => {
                const status = getWarrantyStatus(product);

                switch (activeTab) {
                    case 'active':
                        return status.status === 'Active';
                    case 'expiring':
                        return status.status === 'Expiring Soon';
                    case 'expired':
                        return status.status === 'Expired';
                    default:
                        return true;
                }
            });
        }

        setFilteredProducts(filtered);
    }, [products, searchTerm, activeTab]);

    const loadProducts = async () => {
        setLoading(true);
        try {
            const response = await getCustomerProduct();
            if (response.status === 200) {
                const productData = Array.isArray(response.data?.data) ? response.data.data : [];
                setProducts(productData);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    };

    const getWarrantyStatus = (product) => {
        // Use backend warranty_status and warranty_expiry_date
        const backendStatus = product.warranty_status;
        const warrantyExpiryDate = product.warranty_expiry_date;

        // If no warranty expiry date is provided, fallback to expired
        if (!warrantyExpiryDate) {
            return {
                status: backendStatus || 'Expired',
                color: 'error',
                daysLeft: 0,
            };
        }

        // Calculate days left using warranty_expiry_date from backend
        const expiryDate = dayjs(warrantyExpiryDate);
        const daysLeft = expiryDate.diff(dayjs(), 'days');
        const isActive = dayjs().isBefore(expiryDate);

        console.log('days left', daysLeft);
        console.log('is active', isActive);
        console.log('expiry date', expiryDate);
        // If warranty has expired
        if (!isActive || daysLeft <= 0) {
            return {
                status: 'Expired',
                color: 'error',
                daysLeft: 0,
                expiredAt: expiryDate,
            };
        }

        // If warranty expires within 30 days, mark as "Expiring Soon"
        if (daysLeft <= 30) {
            return {
                status: 'Expiring Soon',
                color: 'warning',
                daysLeft: daysLeft,
                expiredAt: expiryDate,
            };
        }

        // Use backend status for active warranties, fallback to "Active"
        return {
            status: backendStatus || 'Active',
            color: 'success',
            daysLeft: daysLeft,
            expiredAt: expiryDate,
        };
    };

    const handleImageClick = (imgUrl) => {
        if (imgUrl) {
            setViewerImg(imgUrl);
            setViewerOpen(true);
        }
    };

    const renderProductRow = (record) => {
        const status = getWarrantyStatus(record);
        return (
            <tr key={record.id || record.inventory?.serial_number} className="border-t border-t-[#dbdde6]">
                <td className="h-[72px] px-4 py-2">
                    {record.inventory?.product?.images_url ? (
                        <img
                            src={record.inventory.product.images_url}
                            alt={record.inventory?.product?.name || 'Product'}
                            className="w-12 h-12 rounded object-cover bg-gray-100 border border-gray-200 cursor-pointer"
                            style={{ minWidth: 48, minHeight: 48 }}
                            onClick={() => handleImageClick(record.inventory.product.images_url)}
                        />
                    ) : (
                        <div className="w-12 h-12 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-center text-gray-400 text-xs">
                            No Image
                        </div>
                    )}
                </td>
                <td className="h-[72px] px-4 py-2 text-[#111218] text-sm font-normal leading-normal">
                    {record.inventory?.product?.name}
                </td>
                <td className="h-[72px] px-4 py-2 text-[#5f678c] text-sm font-normal leading-normal">
                    {record.inventory?.product?.variant || 'Standard'}
                </td>
                <td className="h-[72px] px-4 py-2 text-[#5f678c] text-sm font-normal leading-normal">
                    {record.inventory?.serial_number}
                </td>
                <td className="h-[72px] px-4 py-2 text-[#5f678c] text-sm font-normal leading-normal">
                    {dayjs(record.date_of_purchase).format('YYYY-MM-DD')}
                </td>
                <td className="h-[72px] px-4 py-2 text-[#5f678c] text-sm font-bold leading-normal tracking-[0.015em]">
                    <div className="flex min-w-[50px] max-w-[200px] h-8 px-4 items-center justify-center rounded-full border border-[#d0d7e7] bg-[#e7ebf3] text-[#0e121b] text-xs font-medium overflow-hidden cursor-pointer">
                        <span className="truncate">{status.status}</span>
                    </div>
                </td>
            </tr>
        );
    };

    const tabItems = [
        { key: 'all', label: 'All Products' },
        { key: 'active', label: 'Active' },
        { key: 'expiring', label: 'Expiring' },
        { key: 'expired', label: 'Expired' },
    ];

    const getTabCounts = () => {
        const counts = { all: products.length, active: 0, expiring: 0, expired: 0 };

        products.forEach((product) => {
            const status = getWarrantyStatus(product);
            if (status.status === 'Active') counts.active++;
            else if (status.status === 'Expiring Soon') counts.expiring++;
            else counts.expired++;
        });

        return counts;
    };

    const tabCounts = getTabCounts();

    return (
        <div className="relative flex size-full min-h-screen flex-col bg-white">
            <div className="layout-container flex h-full grow flex-col">
                <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5">
                    <div className="layout-content-container flex flex-col w-full flex-1">
                        {/* Header */}
                        <div className="flex flex-wrap justify-between gap-3 p-4">
                            <div className="flex min-w-72 flex-col gap-3">
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight">
                                    Product Warranties
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal">
                                    {t('label.warranty_page_description')}
                                </p>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="mb-6 px-4 border-b border-[#dbdde6]">
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                className="!mb-0"
                                items={tabItems.map((item) => ({
                                    ...item,
                                    label: `${item.label} ${tabCounts[item.key] ? `(${tabCounts[item.key]})` : ''}`,
                                }))}
                            />
                        </div>

                        {/* Table */}
                        <div className="px-4 py-3">
                            <Spin spinning={loading}>
                                {/* Desktop Table */}
                                <div className="hidden md:flex overflow-x-auto rounded-xl border border-[#dbdde6] bg-white">
                                    <table className="w-full min-w-[800px]">
                                        <thead>
                                            <tr className="bg-white">
                                                <th className="px-4 py-3 text-left text-[#111218] text-sm font-medium leading-normal">
                                                    Image
                                                </th>
                                                <th className="px-4 py-3 text-left text-[#111218] text-sm font-medium leading-normal">
                                                    Product Name
                                                </th>
                                                <th className="px-4 py-3 text-left text-[#111218] text-sm font-medium leading-normal">
                                                    Variant
                                                </th>
                                                <th className="px-4 py-3 text-left text-[#111218] text-sm font-medium leading-normal">
                                                    Serial Number
                                                </th>
                                                <th className="px-4 py-3 text-left text-[#111218] text-sm font-medium leading-normal">
                                                    Date of Purchase
                                                </th>
                                                <th className="px-4 py-3 text-left text-[#5f678c] text-sm font-medium leading-normal">
                                                    Warranty Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredProducts.length > 0 ? (
                                                filteredProducts.map(renderProductRow)
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="h-[200px]">
                                                        <div className="flex items-center justify-center h-full ">
                                                            <Empty
                                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                                description={
                                                                    searchTerm || activeTab !== 'all'
                                                                        ? 'No products match your current filters'
                                                                        : 'No products registered yet'
                                                                }
                                                            >
                                                                {!searchTerm && activeTab === 'all' && (
                                                                    <Button
                                                                        variant="outline"
                                                                        onClick={() =>
                                                                            navigate('/member/register-product')
                                                                        }
                                                                    >
                                                                        Register Product
                                                                    </Button>
                                                                )}
                                                            </Empty>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card Layout */}
                                <div className="md:hidden space-y-4">
                                    {filteredProducts.length > 0 ? (
                                        filteredProducts.map((record) => {
                                            const status = getWarrantyStatus(record);
                                            return (
                                                <div
                                                    key={record.id || record.inventory?.serial_number}
                                                    className="bg-white border border-[#dbdde6] rounded-xl p-4 space-y-3"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        {record.inventory?.product?.images_url ? (
                                                            <img
                                                                src={record.inventory.product.images_url}
                                                                alt={record.inventory?.product?.name || 'Product'}
                                                                className="w-14 h-14 rounded object-cover bg-gray-100 border border-gray-200 flex-shrink-0 cursor-pointer"
                                                                onClick={() =>
                                                                    handleImageClick(
                                                                        record.inventory.product.images_url,
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="w-14 h-14 rounded bg-gray-100 border border-gray-200 flex items-center text-center justify-center text-gray-400 text-xs flex-shrink-0">
                                                                No Image
                                                            </div>
                                                        )}
                                                        <div className="flex-1">
                                                            <h3 className="text-[#111218] text-base font-medium leading-normal pr-2">
                                                                {record.inventory?.product?.name}
                                                            </h3>
                                                            <div className="flex min-w-[80px] h-6 px-3 items-center justify-center rounded-full border border-[#d0d7e7] bg-[#e7ebf3] text-[#0e121b] text-xs font-medium mt-1">
                                                                <span className="truncate">{status.status}</span>
                                                            </div>
                                                            <div className="mt-2 space-y-1">
                                                                <div className="flex justify-between">
                                                                    <span className="text-[#5f678c] text-sm">
                                                                        Variant:
                                                                    </span>
                                                                    <span className="text-[#111218] text-sm font-medium">
                                                                        {record.inventory?.product?.variant ||
                                                                            'Standard'}
                                                                    </span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-[#5f678c] text-sm">
                                                                        Serial:
                                                                    </span>
                                                                    <span className="text-[#111218] text-sm font-mono">
                                                                        {record.inventory?.serial_number}
                                                                    </span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-[#5f678c] text-sm">
                                                                        Purchase Date:
                                                                    </span>
                                                                    <span className="text-[#111218] text-sm">
                                                                        {dayjs(record.date_of_purchase).format(
                                                                            'YYYY-MM-DD',
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-12">
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={
                                                    searchTerm || activeTab !== 'all'
                                                        ? 'No products match your current filters'
                                                        : 'No products registered yet'
                                                }
                                            >
                                                {!searchTerm && activeTab === 'all' && (
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => navigate('/member/register-product')}
                                                        className="mx-auto"
                                                    >
                                                        Register Product
                                                    </Button>
                                                )}
                                            </Empty>
                                        </div>
                                    )}
                                </div>
                            </Spin>
                        </div>
                    </div>
                </div>
            </div>
            {/* Image Viewer Modal */}
            {viewerOpen && (
                <Modal isOpen={viewerOpen} onClose={() => setViewerOpen(false)} size="small" title={null}>
                    <div className="relative">
                        <button
                            onClick={() => setViewerOpen(false)}
                            className="absolute -top-10 -right-10 z-10 p-2 rounded-full bg-white hover:bg-gray-100 border border-gray-200 shadow"
                            aria-label="Close image viewer"
                            type="button"
                        >
                            <svg
                                className="w-6 h-6 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                        <div className="flex items-center justify-center">
                            {viewerImg && (
                                <img
                                    src={viewerImg}
                                    alt="Product"
                                    className="max-h-[70vh] max-w-full rounded-xl object-contain"
                                />
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ViewProductWarranty;
